import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const labelCls = "text-xs uppercase tracking-[0.15em] text-muted-foreground";

export function ForgotPassword({ initialEmail, onBack }) {
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async (fn) => {
    setError(""); setLoading(true);
    try { await fn(); } catch (e) { setError(apiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  const requestCode = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message); setStep("code");
    });
  };

  const reset = (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError("Passwords don't match"); return; }
    run(async () => {
      await api.post("/auth/reset-password", { email, code, new_password: pw });
      toast.success("Password updated. Please sign in.");
      onBack();
    });
  };

  return (
    <form onSubmit={step === "email" ? requestCode : reset} className="w-full max-w-sm animate-fade-up" data-testid="forgot-form">
      <div className="flex items-center gap-2 mb-8">
        <span className="h-10 w-10 rounded-md bg-primary flex items-center justify-center text-primary-foreground"><KeyRound className="h-5 w-5" /></span>
        <span className="font-head text-xl font-700 tracking-tight">Reset password</span>
      </div>
      <h1 className="font-head text-3xl font-700 tracking-tight mb-1">{step === "email" ? "Forgot password?" : "Enter your code"}</h1>
      <p className="text-muted-foreground text-sm mb-8" data-testid="forgot-message">
        {step === "email" ? "We'll text a 6-digit code to the mobile saved on your account." : msg}
      </p>
      <div className="space-y-4">
        {step === "email" ? (
          <div>
            <Label htmlFor="fp-email" className={labelCls}>Email</Label>
            <Input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" className="mt-1.5" required />
          </div>
        ) : (
          <>
            <div>
              <Label htmlFor="fp-code" className={labelCls}>6-digit code</Label>
              <Input id="fp-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                data-testid="reset-code-input" className="mt-1.5 tracking-[0.5em] text-center text-lg" placeholder="000000" required />
            </div>
            <div>
              <Label htmlFor="fp-pw" className={labelCls}>New password (8+ characters)</Label>
              <Input id="fp-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new-password-input" className="mt-1.5" minLength={8} required />
            </div>
            <div>
              <Label htmlFor="fp-pw2" className={labelCls}>Confirm new password</Label>
              <Input id="fp-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-confirm-password-input" className="mt-1.5" minLength={8} required />
            </div>
          </>
        )}
        {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
        <Button type="submit" disabled={loading || (step === "code" && code.length < 6)} data-testid={step === "email" ? "send-reset-code-button" : "reset-password-submit-button"} className="w-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : step === "email" ? "Text me a code" : "Set new password"}
        </Button>
        {step === "code" && (
          <button type="button" onClick={() => { setStep("email"); setCode(""); setError(""); }} data-testid="resend-code-button"
            className="w-full text-sm text-muted-foreground hover:text-foreground">Didn't get it? Send a new code</button>
        )}
        <button type="button" onClick={onBack} data-testid="forgot-back-button"
          className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </button>
      </div>
    </form>
  );
}
